/**
 * Process-lifetime cache for the usage dashboards' reads.
 *
 * WHY: both usage pages mount fresh on every navigation, so leaving the dashboard and coming
 * back re-ran every query — up to 90 rollup docs, the whole chartmind_user_stats collection,
 * and a name lookup per visible row, each time. Nothing on these pages changes minute to
 * minute, so paying for those reads on every visit is pure waste on a plan with a daily read
 * quota. State inside the component cannot help: it dies with the component. This lives in the
 * module, so it survives unmount the same way a provider mounted above <Routes> would, without
 * having to thread a provider through two separate route trees.
 *
 * Deliberately not persisted to storage. These are aggregates over an organization's clinical
 * activity, and sessionStorage would leave them on a shared workstation after logout.
 *
 * Staleness is bounded by a short TTL. There is deliberately no manual refresh: these numbers
 * move slowly enough that a stale minute never changes a decision, and invalidateUsageCache()
 * is the escape hatch when something really must be dropped (logout does exactly that).
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000;

/** key → { value, expiresAt } and/or { promise } while a load is in flight. */
const store = new Map();

/**
 * Read `key` from the cache, or run `loader` and cache what it resolves to.
 *
 * Concurrent callers for the same key share one in-flight request rather than each issuing
 * their own — the two usage pages both ask for labels as soon as they render. A rejected load
 * is not cached, so a transient failure doesn't stick until the TTL expires.
 */
export async function cachedRead(key, loader, { ttlMs = DEFAULT_TTL_MS } = {}) {
  const hit = store.get(key);

  if (hit && hit.expiresAt > Date.now()) return hit.value;
  if (hit?.promise) return hit.promise;

  const promise = loader()
    .then((value) => {
      store.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    })
    .catch((error) => {
      store.delete(key);
      throw error;
    });

  // Keep any existing entry so a second caller arriving mid-flight finds the promise.
  store.set(key, { ...(hit || {}), promise });
  return promise;
}

/**
 * Drop cached entries.
 *
 * With no argument, drops everything — call it on logout so the next account does not read the
 * previous one's aggregates. With a prefix, drops one family of keys.
 */
export function invalidateUsageCache(keyPrefix) {
  if (!keyPrefix) {
    store.clear();
    return;
  }
  [...store.keys()].forEach((key) => {
    if (key.startsWith(keyPrefix)) store.delete(key);
  });
}

/** Test seam. Not for production code — use invalidateUsageCache. */
export const __cacheSize = () => store.size;
